/**
 * A set budget, in dollars, and the hours it buys (Estimate Tool 8.11; 2026-09-23).
 *
 * The schedule's size has three sources now: the analogs' weighted median hours, a target the
 * estimator typed, and a budget. A budget is dollars; a schedule is quarter-hours by service; a
 * rate sheet — one hourly rate per service code — is the exchange rate between them. Pure: rates
 * in, arithmetic out, and every number here is reproducible in a spreadsheet, which is how the
 * gate checks it.
 *
 * Two things a reader should know.
 *
 * **The budget sets the target hours, and the plan then hits the target exactly.** A template
 * costs `Σ hours × rate` at the chosen sheet; the budget over that cost is the scale; the scale
 * times the template's hours, rounded to the increment, is the target. Everything downstream is
 * the same as for a typed target, so `reconciles` still means what it meant.
 *
 * **A plan of quarter-hour lines cannot always land on a dollar.** The achievable amounts step
 * by a quarter of an hour at some rate — $32.50 to $62.50 on this account — so after scaling and
 * rounding the plan is a few dollars off the budget. `fitToBudget` closes that gap by moving a
 * quarter hour at a time between two lines of different rates, which changes the dollars by a
 * quarter of the rate difference and the hours not at all. It stops when no move improves the
 * residual, and reports what is left: on this account's rates the residual is under $1.25 for
 * any budget that is a multiple of $1.25, and the screen shows it rather than rounding it away.
 */
import { roundTo } from './schedule.js';

const isRemoved = (x) => Boolean(x?.removed ?? x?.removed_by_user);
const cents = (n) => Math.round(n * 100) / 100;

/** A rate lookup from whatever shape the caller holds: a Map, or a plain code → rate object. */
export function rateLookup(rates) {
  if (rates instanceof Map) return (code) => rates.get(code);
  return (code) => rates?.[code];
}

/**
 * What a set of tasks costs at a sheet: `Σ hours × rate` over every live line.
 *
 * A line whose service has no rate on the sheet is not silently priced at zero — it is named,
 * because a plan whose dollars omit a service would read as under budget when it is unpriced.
 *
 * @param {object[]} tasks
 * @param {Map<string, number>|Record<string, number>} rates
 * @returns {{ amount: number, hours: number, unrated: { service_code: string, hours: number }[] }}
 */
export function planAmount(tasks, rates) {
  const rate = rateLookup(rates);
  let amount = 0;
  let hours = 0;
  const unrated = new Map();
  for (const t of tasks ?? []) {
    if (isRemoved(t)) continue;
    for (const a of t.assignments ?? []) {
      if (isRemoved(a)) continue;
      const h = Number(a.hours) || 0;
      const r = rate(a.service_code);
      hours += h;
      if (r === undefined || r === null || !Number.isFinite(Number(r))) {
        unrated.set(a.service_code, (unrated.get(a.service_code) ?? 0) + h);
        continue;
      }
      amount += h * Number(r);
    }
  }
  return {
    amount: cents(amount),
    hours: Math.round(hours * 1e4) / 1e4,
    unrated: [...unrated].map(([service_code, h]) => ({ service_code, hours: h })),
  };
}

/**
 * The target hours a budget buys against a template (8.11).
 *
 * ```
 * template_amount = Σ template hours × rate
 * blended_rate    = template_amount / template_hours
 * target_hours    = budget / blended_rate, rounded to hours_rounding
 * ```
 *
 * The blended rate is the template's own mix of services at this sheet, so a Medium — heavy on
 * development — buys fewer hours per dollar than a Microsite, which is right.
 *
 * @param {object[]} templateTasks  re-homed template tasks, in display order
 * @param {Map<string, number>|Record<string, number>} rates
 * @param {number} budget
 * @param {number} increment  `hours_rounding`
 */
export function targetHoursForBudget(templateTasks, rates, budget, increment) {
  const priced = planAmount(templateTasks, rates);
  if (priced.unrated.length) {
    const err = new Error(
      `the rate sheet has no rate for ${priced.unrated.map((u) => u.service_code).join(', ')}, so the budget cannot be turned into hours`,
    );
    err.code = 'RATE_MISSING';
    err.service_codes = priced.unrated.map((u) => u.service_code);
    throw err;
  }
  if (!(priced.amount > 0) || !(priced.hours > 0)) {
    const err = new Error('the template carries no priced hours, so no budget can scale it');
    err.code = 'TEMPLATE_UNPRICED';
    throw err;
  }
  const blended = priced.amount / priced.hours;
  return {
    template_amount: priced.amount,
    template_hours: priced.hours,
    blended_rate: cents(blended),
    target_hours: roundTo(Number(budget) / blended, increment),
    unrounded_target_hours: Number(budget) / blended,
  };
}

/**
 * Close the gap between a plan's dollars and its budget without changing its hours.
 *
 * Each move takes one increment off a line at one rate and puts it on a line at another, so the
 * total hours are unchanged and the dollars move by `increment × (rate_to − rate_from)`. The move
 * chosen each round is the one that leaves the smallest residual, and the loop stops as soon as
 * no move improves it. Bounded by `maxMoves`, because this is a finishing touch on a quarter-hour
 * grid and not a reshaping of the service mix — on this account it takes one to three moves.
 *
 * A line only gives what it has: nothing goes below zero, and a zero-hour structural line never
 * receives hours, because a fifteen-minute client approval is the thing 8.5 exists to prevent.
 *
 * @param {object[]} tasks     scaled plan tasks, mutated in place and returned
 * @param {Map<string, number>|Record<string, number>} rates
 * @param {number} budget
 * @param {number} increment
 * @param {{ maxMoves?: number }} [opts]
 * @returns {{ tasks: object[], amount: number, residual: number, moves: object[] }}
 */
export function fitToBudget(tasks, rates, budget, increment, { maxMoves = 25 } = {}) {
  const rate = rateLookup(rates);
  const lines = [];
  for (const t of tasks) {
    if (isRemoved(t)) continue;
    for (const a of t.assignments ?? []) {
      if (isRemoved(a)) continue;
      const r = rate(a.service_code);
      if (r === undefined || r === null || !Number.isFinite(Number(r))) continue;
      // Lines that carry hours are the only ones that may give or take: structure stays at zero.
      if (!(Number(a.hours) > 0)) continue;
      lines.push({ task: t, line: a, rate: Number(r) });
    }
  }
  const moves = [];
  let amount = planAmount(tasks, rates).amount;
  let residual = cents(Number(budget) - amount);

  for (let round = 0; round < maxMoves && lines.length > 1; round++) {
    let best = null;
    for (const from of lines) {
      if (from.line.hours < increment - 1e-9) continue;
      for (const to of lines) {
        if (to === from || to.rate === from.rate) continue;
        const delta = increment * (to.rate - from.rate);
        const next = cents(residual - delta);
        if (
          Math.abs(next) < Math.abs(residual) - 1e-9 &&
          (!best || Math.abs(next) < Math.abs(best.next))
        ) {
          best = { from, to, delta, next };
        }
      }
    }
    if (!best) break;
    best.from.line.hours = roundTo(best.from.line.hours - increment, increment);
    best.to.line.hours = roundTo(best.to.line.hours + increment, increment);
    moves.push({
      hours: increment,
      from: {
        task: best.from.task.name,
        service_code: best.from.line.service_code,
        rate: best.from.rate,
      },
      to: { task: best.to.task.name, service_code: best.to.line.service_code, rate: best.to.rate },
      dollars: cents(best.delta),
    });
    residual = best.next;
  }

  // Task totals follow their lines, as everywhere else.
  for (const t of tasks) {
    if (isRemoved(t)) continue;
    t.hours =
      Math.round(
        (t.assignments ?? [])
          .filter((a) => !isRemoved(a))
          .reduce((s, a) => s + (Number(a.hours) || 0), 0) * 1e4,
      ) / 1e4;
  }
  amount = planAmount(tasks, rates).amount;
  return { tasks, amount, residual: cents(Number(budget) - amount), moves };
}

/**
 * The dollars of a plan as it now stands, task by task (8.11): each task's own amount — its live
 * lines at the frozen rates — the roll-up a group is checked against, and the total. The same
 * three shapes `planTotals` gives for hours, under the same rule: a removed line contributes
 * nothing, and a line under a removed task contributes nothing.
 *
 * @param {object[]} tasks
 * @param {Map<string, number>|Record<string, number>|null} rates
 * @returns {{ own: Map<number, number>, rollup: Map<number, number>, total: number, unrated_hours: number }}
 */
export function planAmounts(tasks, rates) {
  const rate = rateLookup(rates ?? {});
  const byOrder = new Map((tasks ?? []).map((t) => [t.display_order, t]));
  const alive = (task) => {
    for (let node = task, guard = 0; node && guard < 50; guard++) {
      if (isRemoved(node)) return false;
      if (node.parent_order === null || node.parent_order === undefined) return true;
      node = byOrder.get(node.parent_order);
    }
    return true;
  };
  const own = new Map();
  const rollup = new Map();
  let total = 0;
  let unrated = 0;
  for (const t of tasks ?? []) {
    let amount = 0;
    if (alive(t)) {
      for (const a of t.assignments ?? []) {
        if (isRemoved(a)) continue;
        const r = rate(a.service_code);
        const h = Number(a.hours) || 0;
        if (r === undefined || r === null || !Number.isFinite(Number(r))) {
          unrated += h;
          continue;
        }
        amount += h * Number(r);
      }
    }
    own.set(t.display_order, cents(amount));
    total += amount;
    // A task's amount rolls up to every group above it.
    for (let p = t.parent_order; p !== null && p !== undefined; p = byOrder.get(p)?.parent_order) {
      rollup.set(p, cents((rollup.get(p) ?? 0) + amount));
      if (!byOrder.has(p)) break;
    }
    rollup.set(t.display_order, cents((rollup.get(t.display_order) ?? 0) + amount));
  }
  return { own, rollup, total: cents(total), unrated_hours: Math.round(unrated * 1e4) / 1e4 };
}
